// @ts-nocheck

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient, configureDataSource, fetchDataSourceStatus, triggerDataSourceSync, getApiBaseUrl } from '../../services/api';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUser, faEnvelope, faPhone, faGlobe, faLock, faSpinner, faCheck, 
    faArrowLeft, faArrowRight, faRobot, faSpider, faExclamationTriangle,
    faEye, faEyeSlash, faBuilding, faBriefcase, faCircleCheck, faCircle,
    faClipboardList, faGears, faCode, faShoppingCart, faHandshake,
    faChalkboardTeacher, faQuestion, faPen, faCubes, faDatabase,
    faFileAlt, faClock, faCheckCircle, faTimesCircle, faInfoCircle,
    faServer, faNetworkWired, faSitemap, faLayerGroup, faStop
} from '@fortawesome/free-solid-svg-icons';
import { stopCrawl, abortCrawl, createCrawlStatusStream } from '../../services/api';
import CrawlStatusMonitor from '../../components/CrawlStatusMonitor';
import { addCrawlJob, getActiveCrawlJobs, subscribeToStatusUpdates, cleanupStaleCrawlJobs, updateCrawlJob } from '../../services/crawlStatusService';
import BotPurposeSelector from '../../components/BotPurposeSelector';
import { BOT_PURPOSE_PROMPTS } from '../../utils/bot_prompts_client';
import { BOT_PERSONALITIES } from '../../utils/bot_personalities';
import { InlineCategoryCard } from '../../components/common';
import DataSourceSelector from '../../components/DataSourceSelector';
import { DataSourceProgress } from '../../components/DataSourceProgress';
import { UnifiedProgressUI } from '../../components/CrawlProgress/UnifiedProgressUI';

interface ClientFormData {
    name: string;
    prompt_display_name: string;
    email: string;
    phone: string;
    website: string;
    password: string;
}

const MAX_PAGE_SELECTION = 1000;

const normalizeBotTypeKey = (type?: string) => {
    if (!type) return '';
    return type.toString().trim().toLowerCase().replace(/\s+/g, '-');
};

const extractCategoryParts = (name: string): string[] =>
    (name || '')
        .split('>')
        .map(part => part.trim())
        .filter(Boolean);

const getLeafCategoryName = (name: string): string => {
    const parts = extractCategoryParts(name);
    return parts.length ? parts[parts.length - 1] : (name || 'Uncategorized');
};

const parseNumericValue = (value: unknown): number | undefined => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim().length) {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }
    return undefined;
};

const clampPercent = (value: number): number => Math.min(100, Math.max(0, Math.round(value)));

type AggregatedCategory = {
    name: string;
    count: number;
    icon?: string;
    rawNames: string[];
    listingCount?: number;
    listingChildrenCount?: number;
    sampleProductUrls?: string[];
};

const aggregateCategoryDetails = (categories: any[]): AggregatedCategory[] => {
    const map = new Map<string, AggregatedCategory>();

    categories.forEach(cat => {
        const displayName = getLeafCategoryName(cat?.name);
        const existing = map.get(displayName);
        const detailCount =
            typeof cat?.detail_count === 'number'
                ? cat.detail_count
                : typeof cat?.count === 'number'
                    ? cat.count
                    : 0;
        const listingCount =
            typeof cat?.listing_count === 'number' ? cat.listing_count : 0;
        const listingChildrenCount =
            typeof cat?.listing_children_count === 'number' ? cat.listing_children_count : 0;
        const sampleProducts: string[] = Array.isArray(cat?.sample_product_urls)
            ? cat.sample_product_urls.filter((url: unknown): url is string => typeof url === 'string')
            : [];

        if (existing) {
            existing.count += detailCount;
            existing.listingCount = (existing.listingCount || 0) + listingCount;
            existing.listingChildrenCount =
                (existing.listingChildrenCount || 0) + listingChildrenCount;
            if (cat?.icon && !existing.icon) {
                existing.icon = cat.icon;
            }
            if (cat?.name && !existing.rawNames.includes(cat.name)) {
                existing.rawNames.push(cat.name);
            }
            if (sampleProducts.length) {
                const seen = new Set(existing.sampleProductUrls || []);
                sampleProducts.forEach(url => {
                    if (!seen.has(url)) {
                        seen.add(url);
                        existing.sampleProductUrls = [...(existing.sampleProductUrls || []), url];
                    }
                });
            }
        } else {
            map.set(displayName, {
                name: displayName,
                count: detailCount,
                icon: cat?.icon,
                rawNames: cat?.name ? [cat.name] : [],
                listingCount: listingCount || undefined,
                listingChildrenCount: listingChildrenCount || undefined,
                sampleProductUrls: sampleProducts.length ? [...sampleProducts] : undefined
            });
        }
    });

    return Array.from(map.values())
        .map(cat => ({
            ...cat,
            icon: cat.icon || '📄'
        }))
        .sort((a, b) => b.count - a.count);
};

interface DiscoverySummary {
    unique_discovered?: number;
    duplicates_skipped?: number;
    preview_pages_total?: number;
    filtered_for_bot?: number;
    max_preview_limit?: number;
}

const formatStatNumber = (value?: number): string | null => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
        return null;
    }
    return value.toLocaleString();
};

const ClientForm: React.FC = () => {
    // Add custom CSS for progress animation
    React.useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes progress {
                0% { width: 0%; }
                100% { width: 100%; }
            }
            .animate-progress {
                animation: progress 2s ease-in-out infinite;
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);
    const navigate = useNavigate();
    const [formData, setFormData] = useState<ClientFormData>({
        name: '',
        prompt_display_name: '',
        email: '',
        phone: '',
        website: '',
        password: ''
    });
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [clientId, setClientId] = useState<string | null>(null);
    const [showProgress, setShowProgress] = useState(false);
    const [hasActiveCrawl, setHasActiveCrawl] = useState(false);
    const [activeCrawlClientId, setActiveCrawlClientId] = useState<string | null>(null);
    const [botPurpose, setBotPurpose] = useState<string>('general');
    const [botPersonality, setBotPersonality] = useState<string>('professional');
    const [customPrompt, setCustomPrompt] = useState<string>(BOT_PURPOSE_PROMPTS['general'].prompt);
    const [showPromptEditor, setShowPromptEditor] = useState<boolean>(false);
    const [isPreScanning, setIsPreScanning] = useState<boolean>(false);
    const [preScannedPages, setPreScannedPages] = useState<any[]>([]);
    const [selectedPages, setSelectedPages] = useState<Set<string>>(new Set());
    const [hasManualSelection, setHasManualSelection] = useState<boolean>(false);
    const [categoriesDetail, setCategoriesDetail] = useState<any[]>([]);
    const [totalDiscoveredPages, setTotalDiscoveredPages] = useState<number>(0);
    const [totalAnalyzedPages, setTotalAnalyzedPages] = useState<number>(0);
    const [discoverySummary, setDiscoverySummary] = useState<DiscoverySummary | null>(null);
    const [activeCategoryTooltip, setActiveCategoryTooltip] = useState<string | null>(null);
    const aggregatedCategories = React.useMemo(
        () => aggregateCategoryDetails(categoriesDetail),
        [categoriesDetail]
    );
    const categoryPagesMap = React.useMemo(() => {
        if (!aggregatedCategories.length || !preScannedPages.length) {
            return new Map<string, any[]>();
        }

        const map = new Map<string, any[]>();
        const seenUrls = new Map<string, Set<string>>();

        aggregatedCategories.forEach(cat => {
            map.set(cat.name, []);
            seenUrls.set(cat.name, new Set());
        });

        preScannedPages.forEach(page => {
            if (!page || !page.url || !page.category) return;
            const leafName = getLeafCategoryName(page.category).toLowerCase();
            aggregatedCategories.forEach(cat => {
                const matchesLeaf = leafName === cat.name.toLowerCase();
                const matchesRaw = cat.rawNames.some(raw => raw && raw.toLowerCase() === page.category.toLowerCase());
                if (matchesLeaf || matchesRaw) {
                    const seenForCategory = seenUrls.get(cat.name);
                    if (seenForCategory && !seenForCategory.has(page.url)) {
                        seenForCategory.add(page.url);
                        map.get(cat.name)?.push(page);
                    }
                }
            });
        });

        return map;
    }, [aggregatedCategories, preScannedPages]);

    React.useEffect(() => {
        setHasManualSelection(false);
    }, [botPurpose]);
    const [currentStep, setCurrentStep] = useState<'client-details' | 'data-source-selection' | 'data-source-credentials' | 'data-source-syncing' | 'page-scanning' | 'bot-selection' | 'page-selection' | 'crawling'>('client-details');
    React.useEffect(() => {
        setActiveCategoryTooltip(null);
    }, [currentStep, aggregatedCategories.length]);
    const [isScanningPages, setIsScanningPages] = useState<boolean>(false);
    const [crawlStatus, setCrawlStatus] = useState<any>(null);
    const [isStoppingCrawl, setIsStoppingCrawl] = useState<boolean>(false);
    const [isAbortingCrawl, setIsAbortingCrawl] = useState<boolean>(false);

    // Data source selection states
    const [selectedDataSource, setSelectedDataSource] = useState<string | null>(null);
    const [dataSourceConfig, setDataSourceConfig] = useState<any>(null);
    const [pendingDataSource, setPendingDataSource] = useState<{ method: string; config?: any } | null>(null);
    const [shopifyCredentials, setShopifyCredentials] = useState({
        shopDomain: '',
        accessToken: '',
        apiVersion: '2024-01',
        autoSyncEnabled: true,
        syncFrequencyHours: 6
    });
    const [wooCredentials, setWooCredentials] = useState({
        storeUrl: '',
        consumerKey: '',
        consumerSecret: '',
        autoSyncEnabled: true,
        syncFrequencyHours: 6
    });
    const [feedCredentials, setFeedCredentials] = useState({
        feedUrl: '',
        autoSyncEnabled: true,
        syncFrequencyHours: 24
    });
    const [dataSourceStatus, setDataSourceStatus] = useState<any>(null);
    const [dataSourceSyncState, setDataSourceSyncState] = useState<'idle' | 'syncing' | 'success' | 'error'>('idle');
    const [dataSourceSyncSummary, setDataSourceSyncSummary] = useState<any>(null);
    const [dataSourceError, setDataSourceError] = useState<string | null>(null);
    const [savingDataSource, setSavingDataSource] = useState<boolean>(false);
    const dataSourcePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const postSyncTransitionRef = useRef<boolean>(false);

    // Data source streaming progress states
    const [dataSourceProgress, setDataSourceProgress] = useState<number>(0);
    const [dataSourcePhase, setDataSourcePhase] = useState<string>('');
    const [dataSourceMessage, setDataSourceMessage] = useState<string>('');
    const [productsStored, setProductsStored] = useState<number>(0);
    const [productsTotal, setProductsTotal] = useState<number>(0);
    const [productsFailed, setProductsFailed] = useState<number>(0);
    const [embeddingProgress, setEmbeddingProgress] = useState<number>(0);
    const dataSourceEventSourceRef = useRef<EventSource | null>(null);

    // Page discovery UI states
    const [discoveredPages, setDiscoveredPages] = useState<any[]>([]);
    const [scanProgress, setScanProgress] = useState<number>(0);
    const [scanMessage, setScanMessage] = useState<string>('');

    // Reference to track component mounted state
    const isMounted = useRef(true);

    // Enhanced status tracking (like CrawlDetails)
    const prevCompletedRef = useRef<number>(0);
    const prevVisitedRef = useRef<string[]>([]);

    // Computed values for enhanced display (like CrawlDetails)
    const totalProcessedPages = React.useMemo(() => {
        if (!crawlStatus) return 0;
        const { pages_scraped, pages_crawled, visited_urls_total, visited_urls } = crawlStatus;

        // Priority 1: Use pages_scraped for cumulative count across all chunks
        if (typeof pages_scraped === 'number' && pages_scraped >= 0) {
            return pages_scraped;
        }
        // Priority 2: Use pages_crawled
        if (typeof pages_crawled === 'number' && pages_crawled >= 0) {
            return pages_crawled;
        }
        // Priority 3: Use visited_urls_total
        if (typeof visited_urls_total === 'number' && visited_urls_total >= 0) {
            return visited_urls_total;
        }
        // Priority 4: Use visited_urls array length
        if (Array.isArray(visited_urls) && visited_urls.length > 0) {
            return visited_urls.length;
        }
        return 0;
    }, [crawlStatus]);

    const chunkInfo = React.useMemo(() => {
        if (!crawlStatus) {
            return { completed: 0, total: 0, active: 0, inProgress: 0 };
        }

        const rawTotal =
            parseNumericValue(crawlStatus.chunks_total) ??
            parseNumericValue(crawlStatus.chunksTotal) ??
            0;
        const rawCompleted =
            parseNumericValue(crawlStatus.chunks_completed) ??
            parseNumericValue(crawlStatus.chunksCompleted) ??
            0;
        const total = Math.max(0, rawTotal);
        const completed = Math.min(Math.max(0, rawCompleted), total || rawCompleted);

        // Get the number of chunks actively being processed
        const activeChunksCount = parseNumericValue(crawlStatus.active_chunks_count) ?? 0;
        const inProgress = activeChunksCount > 0 ? activeChunksCount : 0;

        const currentChunkRaw = parseNumericValue(
            crawlStatus.current_chunk ?? crawlStatus.currentChunk
        );
        let activeChunk = 0;
        if (typeof currentChunkRaw === 'number' && currentChunkRaw > 0) {
            activeChunk = currentChunkRaw;
        } else if (completed < total && total > 0) {
            activeChunk = completed + 1;
        } else if (total > 0) {
            activeChunk = total;
        }

        if (total > 0) {
            activeChunk = Math.min(Math.max(activeChunk, 1), total);
        }

        return {
            total,
            completed,
            active: activeChunk,
            inProgress
        };
    }, [crawlStatus]);

    const embeddingInfo = React.useMemo(() => {
        if (!crawlStatus) return { current: 0, total: 0, status: null };
        const embedding_current =
            parseNumericValue(crawlStatus.embedding_current) ??
            parseNumericValue(crawlStatus.embeddingCurrent) ??
            0;
        const embedding_total =
            parseNumericValue(crawlStatus.embedding_total) ??
            parseNumericValue(crawlStatus.embeddingTotal) ??
            0;
        const embedding_status = crawlStatus.embedding_status ?? crawlStatus.embeddingStatus ?? null;
        return {
            current: embedding_current ?? 0,
            total: embedding_total ?? 0,
            status: embedding_status ?? null,
        };
    }, [crawlStatus]);

    const progressPercent = React.useMemo(() => {
        if (!crawlStatus) {
            return 0;
        }

        const candidateValues = [
            crawlStatus.progress_percentage,
            crawlStatus.progressPercentage,
            crawlStatus.progress
        ];

        for (const value of candidateValues) {
            const parsed = parseNumericValue(value);
            if (typeof parsed === 'number') {
                return clampPercent(parsed);
            }
        }

        const totalCandidates = [
            crawlStatus.total_pages,
            crawlStatus.totalPages,
            crawlStatus.estimated_total_pages,
            crawlStatus.estimatedTotalPages
        ];
        const totalCandidate = totalCandidates.find(val => {
            const parsed = parseNumericValue(val);
            return typeof parsed === 'number' && parsed > 0;
        });

        const numericTotal =
            typeof totalCandidate !== 'undefined' && totalCandidate !== null
                ? parseNumericValue(totalCandidate) ?? 0
                : 0;

        if (numericTotal > 0) {
            const computed = Math.round((totalProcessedPages / numericTotal) * 100);
            return clampPercent(computed);
        }

        return totalProcessedPages > 0 ? 100 : 0;
    }, [crawlStatus, totalProcessedPages]);

    const pagesFailedCount = React.useMemo(() => {
        if (!crawlStatus) return 0;
        const { pages_failed, errors } = crawlStatus;
        if (typeof pages_failed === 'number') return pages_failed;
        if (Array.isArray(errors)) return errors.length;
        return 0;
    }, [crawlStatus]);

    // Check if user is logged in and is a consultant
    useEffect(() => {
        const token = localStorage.getItem('auth_token');
        const userRole = localStorage.getItem('user_role');
        
        if (!token || (userRole !== 'consultant' && userRole !== 'super')) {
            console.log('[AUTH] No valid consultant token found, redirecting to login');
            navigate('/login');
            return;
        }
        
        // Set up cleanup function
        return () => {
            isMounted.current = false;
        };
    }, [navigate]);

    useEffect(() => {
        return () => {
            // Clean up polling interval
            if (dataSourcePollRef.current) {
                clearInterval(dataSourcePollRef.current);
            }
            // Clean up SSE streaming connection
            if (dataSourceEventSourceRef.current) {
                dataSourceEventSourceRef.current.close();
                dataSourceEventSourceRef.current = null;
            }
        };
    }, []);


    // Check for active crawls when component mounts
    useEffect(() => {
        // Clean up any stale crawl jobs first
        cleanupStaleCrawlJobs();
        
        const checkActiveCrawls = () => {
            const activeJobs = getActiveCrawlJobs();
            const activeCrawl = activeJobs.find(job => 
                ['initializing', 'in_progress', 'embedding'].includes(job.status)
            );
            
            if (activeCrawl) {
                setHasActiveCrawl(true);
                setActiveCrawlClientId(activeCrawl.clientId);
                console.log('[CLIENT-FORM] Active crawl detected for client:', activeCrawl.clientId);
            } else {
                setHasActiveCrawl(false);
                setActiveCrawlClientId(null);
            }
        };
        
        // Check on mount
        checkActiveCrawls();
        
        // Subscribe to status updates to check continuously
        const unsubscribe = subscribeToStatusUpdates(checkActiveCrawls);
        
        return () => {
            unsubscribe();
        };
    }, []);

    // Log state changes for debugging
    useEffect(() => {
        console.log(`[CLIENT-FORM] State changed - loading: ${loading}, showProgress: ${showProgress}, clientId: ${clientId}, success: ${success}`);
    }, [loading, showProgress, clientId, success]);

    // Update prompt when bot purpose changes
    useEffect(() => {
        if (BOT_PURPOSE_PROMPTS[botPurpose]) {
            setCustomPrompt(BOT_PURPOSE_PROMPTS[botPurpose].prompt);
            // Hide prompt editor when bot purpose changes
            setShowPromptEditor(false);
        }
    }, [botPurpose]);

    // Monitor crawl status when in progress - Using createCrawlStatusStream
    useEffect(() => {
        if (showProgress && clientId) {
            console.log('[CLIENT-FORM] Setting up crawl status stream for:', clientId);

            let eventSource: EventSource | null = null;

            // Small delay to ensure Redis status write completes
            // Reduced from 1000ms to 200ms since Redis persistence prevents missing updates
            const streamDelay = setTimeout(() => {
                console.log('[CLIENT-FORM] Starting stream connection...');

                // Use the API helper to create the stream
                eventSource = createCrawlStatusStream(
                    clientId,
                    (data) => {
                        console.log('[CLIENT-FORM] Received crawl status update:', data);

                        // Skip if error or no active crawl
                        if (data?.error || data?.status === 'no_crawl') {
                            console.log('[CLIENT-FORM] No active crawl or error:', data);
                            return;
                        }

                        // Enhanced status processing (like CrawlDetails)
                        setCrawlStatus(prev => {
                            // Track chunks_completed for proper progress
                            const prevCompleted = prev?.chunks_completed ?? prevCompletedRef.current ?? 0;
                            const nextCompleted = data.chunks_completed ?? prevCompleted;
                            if (nextCompleted > prevCompleted) {
                                prevCompletedRef.current = nextCompleted;
                            }

                            // Merge visited URLs to avoid duplicates
                            const prevVisited: string[] = prevVisitedRef.current ?? prev?.visited_urls ?? [];
                            const deltaFromPayload: string[] = Array.isArray(data.newly_visited_urls)
                                ? data.newly_visited_urls
                                : [];
                            const snapshotFromPayload: string[] = Array.isArray(data.visited_urls)
                                ? data.visited_urls
                                : prev?.visited_urls ?? [];

                            const mergedVisited: string[] = [...prevVisited];
                            const appendUrl = (url: string) => {
                                if (!mergedVisited.includes(url)) {
                                    mergedVisited.push(url);
                                }
                            };

                            if (deltaFromPayload.length) {
                                deltaFromPayload.forEach(appendUrl);
                            } else if (snapshotFromPayload.length) {
                                snapshotFromPayload.forEach(appendUrl);
                            }

                            prevVisitedRef.current = mergedVisited;

                            return {
                                ...prev,
                                ...data,
                                chunks_completed: nextCompleted,
                                chunks_total: data.chunks_total ?? prev?.chunks_total,
                                pages_scraped: data.pages_scraped ?? prev?.pages_scraped,
                                visited_urls: mergedVisited,
                                visited_urls_total: data.visited_urls_total ?? mergedVisited.length,
                            };
                        });

                        // Update global crawl job status
                        const activeCrawls = getActiveCrawlJobs();
                        const activeJob = activeCrawls.find(job => job.clientId === clientId);
                        if (activeJob) {
                            const progressFromPayload =
                                parseNumericValue(data.progress_percentage) ??
                                parseNumericValue(data.progressPercentage) ??
                                parseNumericValue(data.progress);

                            let resolvedProgress = progressFromPayload;

                            if (typeof resolvedProgress !== 'number') {
                                const processedEstimate =
                                    parseNumericValue(data.pages_processed) ??
                                    parseNumericValue(data.pages_scraped) ??
                                    parseNumericValue(data.pagesProcessed) ??
                                    parseNumericValue(data.pagesScraped);
                                const totalEstimate =
                                    parseNumericValue(data.total_pages) ??
                                    parseNumericValue(data.totalPages) ??
                                    parseNumericValue(data.estimated_total_pages) ??
                                    parseNumericValue(data.estimatedTotalPages);
                                if (
                                    typeof processedEstimate === 'number' &&
                                    typeof totalEstimate === 'number' &&
                                    totalEstimate > 0
                                ) {
                                    resolvedProgress = clampPercent(
                                        (processedEstimate / totalEstimate) * 100
                                    );
                                }
                            }

                            updateCrawlJob(activeJob.id, {
                                status: data.status,
                                pages_crawled: data.pages_scraped || data.pages_processed || 0,  // Use pages_scraped/processed instead of pages_crawled
                                pages_in_queue: data.pages_in_queue || 0,
                                progress_percentage:
                                    typeof resolvedProgress === 'number'
                                        ? resolvedProgress
                                        : activeJob.progress_percentage || 0,
                                current_url: data.current_url,
                                message: data.message,
                                embedding_status: data.embedding_status
                            });
                        }

                        // Check if crawling is complete
                        if (data.status === 'completed' || data.status === 'failed' || data.status === 'aborted') {
                            console.log('[CLIENT-FORM] Crawl finished with status:', data.status);
                            if (data.status === 'completed') {
                                handleProgressComplete();
                            }
                            if (eventSource) eventSource.close();
                        }
                    },
                    (error) => {
                        console.error('[CLIENT-FORM] EventSource error:', error);
                    }
                );
            }, 200); // 200ms delay for Redis write completion

            return () => {
                console.log('[CLIENT-FORM] Cleaning up crawl status stream');
                clearTimeout(streamDelay);
                if (eventSource) {
                    eventSource.close();
                }
            };
        }
    }, [showProgress, clientId]);

    const handleClientDetailsSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        
        try {
            // Check for active crawls before proceeding
            const activeJobs = getActiveCrawlJobs();
            const activeCrawl = activeJobs.find(job => 
                ['initializing', 'in_progress', 'embedding'].includes(job.status)
            );
            
            if (activeCrawl) {
                setError('A website crawl is currently in progress. Please wait for it to complete before creating another client.');
                toast.warning('Cannot create client while another crawl is in progress');
                setLoading(false);
                
                // Optionally redirect to the active crawl details
                const shouldRedirect = window.confirm('Would you like to view the active crawl progress?');
                if (shouldRedirect) {
                    navigate(`/crawl-details/${activeCrawl.clientId}`);
                }
                return;
            }
            
            // Check if we're still authenticated before submitting
            const token = localStorage.getItem('auth_token');
            if (!token) {
                setError('Session expired. Please login again.');
                setLoading(false); // Reset loading state
                navigate('/login');
                return;
            }

            console.log('[CLIENT-FORM] Submitting client creation form');
            // Include bot personality with client creation
            const response = await createClient({
                ...formData,
                bot_personality: botPersonality
            });
            console.log('[CLIENT-FORM] Client created successfully:', response);
            
            // Store the client ID from the response
            if (response && response.client_id) {
                // First set clientId so it's available for next steps
                setClientId(response.client_id);
                setSuccess(true);
                setLoading(false);
                
                toast.success('Client account created successfully!');

                // Go to data source selection step
                setCurrentStep('data-source-selection');
            } else {
                console.error('[CLIENT-FORM] Invalid response format:', response);
                setError('Failed to get client ID from response');
                toast.error('Failed to get client ID from response');
            }
        } catch (error: any) {
            console.error('[CLIENT-FORM] Error creating client:', error);
            setLoading(false);
            setError(error.message || 'Failed to create client');
            toast.error(error.message || 'Failed to create client');
            setSuccess(false);
            setCurrentStep('client-details');
            return;
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleDataSourceSelected = async (method: string, config?: any) => {
        console.log('[CLIENT-FORM] handleDataSourceSelected called:', {
            method,
            config,
            hasClientId: !!clientId,
            currentStep
        });

        setSelectedDataSource(method);
        setDataSourceConfig(config);

        const normalizeDomain = (value: string) => {
            if (!value) return '';
            let sanitized = value.trim();
            if (!sanitized) return '';
            if (!sanitized.startsWith('http')) {
                sanitized = `https://${sanitized}`;
            }
            try {
                const url = new URL(sanitized);
                return url.hostname.toLowerCase();
            } catch {
                return sanitized.replace(/^https?:\/\//, '').split('/')[0];
            }
        };

        const normalizeUrl = (value: string) => {
            if (!value) return '';
            const trimmed = value.trim();
            if (!trimmed) return '';
            if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
                return trimmed;
            }
            return `https://${trimmed}`;
        };

        if (method === 'shopify_api') {
            // Check if this is from inline submission (credentials already provided)
            if (config?.inline_submitted && config?.shop_domain && config?.access_token) {
                try {
                    await connectShopifyDataSource(config);
                } catch (error) {
                    // Error is already handled in connectShopifyDataSource
                    // Don't redirect to credentials step on error
                    throw error;
                }
                return;
            }

            // Only go to credentials step if no inline credentials were provided
            if (!config?.inline_submitted) {
                setPendingDataSource({ method, config });
                setDataSourceSyncState('idle');
                setDataSourceSyncSummary(null);
                setDataSourceError(null);
                postSyncTransitionRef.current = false;

                if (dataSourcePollRef.current) {
                    clearInterval(dataSourcePollRef.current);
                    dataSourcePollRef.current = null;
                }

                const inferredDomain =
                    config?.shop_domain ||
                    normalizeDomain((formData.website || '').replace(/\/$/, ''));
                setShopifyCredentials(prev => ({
                    ...prev,
                    shopDomain: inferredDomain || prev.shopDomain,
                    autoSyncEnabled: true
                }));

                toast.info('Connect your store to continue');
                setCurrentStep('data-source-credentials');
            }
            return;
        }

        if (method === 'woocommerce_api') {
            // Check if this is from inline submission (credentials already provided)
            if (config?.inline_submitted && config?.store_url && config?.consumer_key && config?.consumer_secret) {
                try {
                    await connectWooDataSource(config);
                } catch (error) {
                    // Error is already handled in connectWooDataSource
                    // Don't redirect to credentials step on error
                    throw error;
                }
                return;
            }

            // Only go to credentials step if no inline credentials were provided
            if (!config?.inline_submitted) {
                setPendingDataSource({ method, config });
                setDataSourceSyncState('idle');
                setDataSourceSyncSummary(null);
                setDataSourceError(null);
                postSyncTransitionRef.current = false;

                if (dataSourcePollRef.current) {
                    clearInterval(dataSourcePollRef.current);
                    dataSourcePollRef.current = null;
                }

                const inferredUrl =
                    config?.store_url ||
                    normalizeUrl((formData.website || '').replace(/\/$/, ''));
                setWooCredentials(prev => ({
                    ...prev,
                    storeUrl: inferredUrl || prev.storeUrl,
                    autoSyncEnabled: true
                }));

                toast.info('Connect your store to continue');
                setCurrentStep('data-source-credentials');
            }
            return;
        }

        // Handle feed source - when URL is provided from DataSourceSelector, start sync directly
        if (method === 'feed') {
            console.log('[CLIENT-FORM] Feed source selected, checking config:', {
                hasConfig: !!config,
                hasFeedUrl: !!config?.feed_url,
                feedUrl: config?.feed_url,
                fullConfig: config
            });

            // Feed always provides URL from DataSourceSelector - no need for credentials step
            if (config?.feed_url) {
                console.log('[CLIENT-FORM] Feed has URL, starting sync directly');

                // Ensure we have a clientId before proceeding
                if (!clientId) {
                    console.error('[CLIENT-FORM] No clientId available for feed configuration');
                    toast.error('Client not created yet. Please try again.');
                    return;
                }

                try {
                    // Set the feed credentials from the config
                    setFeedCredentials({
                        feedUrl: config.feed_url,
                        autoSyncEnabled: config.auto_sync_enabled !== false,
                        syncFrequencyHours: config.sync_frequency_hours || 24
                    });

                    // Connect and start syncing immediately (skip credentials step)
                    console.log('[CLIENT-FORM] Calling connectFeedDataSource with clientId:', clientId);
                    await connectFeedDataSource(config);
                    console.log('[CLIENT-FORM] connectFeedDataSource completed successfully');
                } catch (error) {
                    // Error is already handled in connectFeedDataSource
                    console.error('[CLIENT-FORM] Feed connection failed:', error);
                }
                return; // Important: return here to prevent falling through
            }

            // This shouldn't happen - feed should always have URL from DataSourceSelector
            console.error('[CLIENT-FORM] Feed selected but no URL provided - this should not happen');
            toast.error('Feed URL is required');
            return; // Important: return here to prevent falling through
        }

        // Handle scraping method explicitly
        if (method === 'scraping') {
            console.log('[CLIENT-FORM] Web scraping selected, going to page scanning');
            toast.success('Web Scraping selected');
            setCurrentStep('page-scanning');
            if (clientId && formData.website) {
                await scanWebsitePages(clientId, formData.website);
            }
            return;
        }

        // Any other unhandled method
        console.warn('[CLIENT-FORM] Unhandled data source method:', method);
        toast.error(`Unsupported data source method: ${method}`);
    };

    const handleSkipDataSourceSelection = async () => {
        // User skipped - default to scraping
        setSelectedDataSource('scraping');
        setDataSourceConfig({ source_type: 'scraping' });

        // Proceed to page scanning
        setCurrentStep('page-scanning');
        if (clientId && formData.website) {
            await scanWebsitePages(clientId, formData.website);
        }
    };

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };
    
    const handleProgressComplete = () => {
        // Navigate to dashboard when progress is complete
        navigate('/consultant/dashboard');
    };

    // Store LLM analysis and bot recommendations
    const [llmAnalysis, setLlmAnalysis] = React.useState<any>(null);
    const [botRecommendations, setBotRecommendations] = React.useState<any[]>([]);
    const [recommendedBotType, setRecommendedBotType] = React.useState<string>('');

    // Component mount logging - DON'T reset bot recommendations here
    React.useEffect(() => {
        console.log('[CLIENT-FORM] Component mounted, bot recommendations state initialized');
        // Don't reset state here - let it be set by SSE events
    }, []);

    // Debug logging for bot recommendations
React.useEffect(() => {
    console.log('[CLIENT-FORM] botRecommendations state updated:', botRecommendations);
    console.log('[CLIENT-FORM] recommendedBotType state updated:', recommendedBotType);
    console.log('[CLIENT-FORM] llmAnalysis state updated:', llmAnalysis);
}, [botRecommendations, recommendedBotType, llmAnalysis]);

    const pagesWithCategories = React.useMemo(
        () => preScannedPages.filter(page => page?.category),
        [preScannedPages]
    );

    const getBotSelectionContext = React.useCallback(
        (botTypeKey: string) => {
            const selectedBotRec = botRecommendations.find(
                rec => normalizeBotTypeKey(rec.bot_type) === normalizeBotTypeKey(botTypeKey)
            );

            const botCategories = Array.isArray(selectedBotRec?.category_names)
                ? selectedBotRec?.category_names
                : [];

            console.log('[CLIENT-FORM] Bot filtering:', {
                botTypeKey,
                selectedBotRec,
                botCategories,
                categoryNames: selectedBotRec?.category_names,
                preScannedPagesCount: preScannedPages.length
            });

            const normalizedPurpose = normalizeBotTypeKey(botTypeKey);
            const normalizedRecType = normalizeBotTypeKey(selectedBotRec?.bot_type || '');
            const shouldFilterByCategories =
                preScannedPages.length > 0 &&
                !!selectedBotRec &&
                botCategories.length > 0 &&
                normalizedPurpose !== 'general' &&
                normalizedRecType !== 'general-assistant';

            const filteredPagesCandidate = shouldFilterByCategories
                ? preScannedPages.filter(
                      page => page?.category && botCategories.includes(page.category)
                  )
                : preScannedPages;

            const basePages =
                filteredPagesCandidate.length > 0 ? filteredPagesCandidate : preScannedPages;

            const limit = selectedBotRec?.total_urls
                ? Math.min(selectedBotRec.total_urls, basePages.length, MAX_PAGE_SELECTION)
                : Math.min(basePages.length, MAX_PAGE_SELECTION);

            const limitedPages = limit > 0 ? basePages.slice(0, limit) : basePages;

            const filterApplied =
                shouldFilterByCategories &&
                filteredPagesCandidate.length > 0 &&
                filteredPagesCandidate.length < preScannedPages.length;

            const noCategoryMatches =
                shouldFilterByCategories &&
                filteredPagesCandidate.length === 0 &&
                pagesWithCategories.length > 0;

            return {
                selectedBotRec,
                botCategories,
                shouldFilterByCategories,
                filteredPagesCandidate,
                basePages,
                limitedPages,
                filterApplied,
                noCategoryMatches,
                effectiveTotal: limitedPages.length
            };
        },
        [botRecommendations, pagesWithCategories, preScannedPages]
    );

    // Reset manual selection tracking whenever bot purpose changes
    React.useEffect(() => {
        setHasManualSelection(false);
    }, [botPurpose]);

    // Auto-align selected pages to the active bot type when the user hasn't manually edited the selection
    React.useEffect(() => {
        if (!preScannedPages.length || hasManualSelection) {
            return;
        }

        const { limitedPages } = getBotSelectionContext(botPurpose);

        if (!limitedPages.length) {
            return;
        }

        const filteredUrls = new Set(limitedPages.map((page: any) => page.url));
        const selectionMatches =
            filteredUrls.size === selectedPages.size &&
            Array.from(filteredUrls).every(url => selectedPages.has(url));

        if (!selectionMatches) {
            setSelectedPages(filteredUrls);
        }
    }, [botPurpose, getBotSelectionContext, hasManualSelection, preScannedPages, selectedPages]);

    // Extract title from URL for display
    const extractTitleFromUrl = (url: string): string => {
        try {
            const urlObj = new URL(url);
            const path = urlObj.pathname.replace(/\/$/, '');
            const lastSegment = path.split('/').pop() || 'Home';
            return lastSegment
                .replace(/-/g, ' ')
                .replace(/_/g, ' ')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        } catch {
            return 'Page';
        }
    };

    // Add function to scan website pages with LLM categorization
    const scanWebsitePages = async (clientId: string, websiteUrl: string) => {
        setIsScanningPages(true);
        setDiscoveredPages([]);
        setScanProgress(0);
        setScanMessage('Connecting to server...');
        setPreScannedPages([]);
        setSelectedPages(new Set());
        setHasManualSelection(false);
        setDiscoverySummary(null);

        try {
            // Use SSE for real-time streaming preview
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const token = localStorage.getItem('auth_token');

            const params = new URLSearchParams();
            params.set('url', websiteUrl);
            params.set('is_demo', 'false');
            if (clientId) params.set('client_id', clientId);
            if (recommendedBotType) params.set('bot_type', recommendedBotType);
            if (token) params.set('token', token);

            const streamUrl = `${apiUrl}/api/crawl/preview-stream?${params.toString()}`;

            const eventSource = new EventSource(streamUrl);

            // Handle status updates
            eventSource.addEventListener('status', (e: any) => {
                const data = JSON.parse(e.data);
                setScanMessage(data.message);
                setScanProgress(data.progress || 0);
            });

            // Handle page discovery batches
            eventSource.addEventListener('page_batch', (e: any) => {
                const data = JSON.parse(e.data);
                console.log('[CLIENT-FORM] page_batch received:', {
                    batch_size: data.pages?.length,
                    total_discovered: data.total_discovered,
                    current_displayed: discoveredPages.length
                });
                setDiscoveredPages(prev => {
                    const updated = [...prev, ...data.pages];
                    console.log('[CLIENT-FORM] Updated discovered pages count:', updated.length);
                    return updated;
                });
                setScanProgress(data.progress || 0);
                setScanMessage(`Discovered ${data.total_discovered} pages...`);
            });

            // Handle discovery complete
            eventSource.addEventListener('discovery_complete', (e: any) => {
                const data = JSON.parse(e.data);
                setScanMessage(data.message);
                setScanProgress(data.progress || 40);
            });

            // Handle categorization progress
            eventSource.addEventListener('categorization_progress', (e: any) => {
                const data = JSON.parse(e.data);
                setScanMessage(data.message || '🤖 Categorizing pages with AI...');
                if (typeof data.progress === 'number') {
                    setScanProgress(prev => Math.max(prev, data.progress));
                }
            });

            // Handle categorization complete
            eventSource.addEventListener('categorization_complete', (e: any) => {
                const data = JSON.parse(e.data);
                setScanMessage(`✅ Site type: ${data.site_type}`);
                setScanProgress(data.progress || 70);
            });

            // Handle bot recommendations
            eventSource.addEventListener('bot_recommendations', (e: any) => {
                try {
                    const rawData = e.data;
                    console.log('[CLIENT-FORM] Raw SSE data received:', rawData);
                    const data = JSON.parse(rawData);
                    console.log('[CLIENT-FORM] Parsed bot recommendations:', data);
                    console.log('[CLIENT-FORM] all_bot_types:', data.all_bot_types);

                    if (data.all_bot_types) {
                        data.all_bot_types.forEach((bot: any, index: number) => {
                            console.log(`[CLIENT-FORM] Bot ${index}:`, {
                                bot_type: bot.bot_type,
                                total_urls: bot.total_urls,
                                score: bot.score,
                                category_names: bot.category_names,
                                category_names_length: bot.category_names?.length || 0
                            });
                        });
                    }

                    setBotRecommendations(data.all_bot_types || []);
                    setRecommendedBotType(data.recommended || 'General Assistant');
                    setScanMessage(`🤖 Recommended: ${data.recommended}`);
                    setScanProgress(data.progress || 85);
                } catch (error) {
                    console.error('[CLIENT-FORM] Error parsing bot recommendations:', error);
                    console.error('[CLIENT-FORM] Raw data:', e.data);
                }
            });

            // Handle complete event
            eventSource.addEventListener('complete', (e: any) => {
                const data = JSON.parse(e.data);
                console.log('[CLIENT-FORM] Complete event received:', data);
                console.log('[CLIENT-FORM] Categories detail:', data.categories_detail);
                console.log('[CLIENT-FORM] Categories length:', data.categories_detail?.length || 0);

                setDiscoverySummary(data.discovery_summary || null);

                // Store pages and analysis
                setPreScannedPages(data.preview_pages || []);
                setLlmAnalysis(data.llm_analysis || {});
                setCategoriesDetail(data.categories_detail || []);
                setTotalDiscoveredPages(data.total_discovered || 0);
                setTotalAnalyzedPages(data.total_analyzed || 0);

                // Select all pages by default
                const allUrls = new Set<string>((data.preview_pages || []).map((page: any) => page.url));
                setSelectedPages(allUrls);
                setHasManualSelection(false);

                // Analysis complete - no notification needed (user can see results in UI)

                setScanProgress(100);
                eventSource.close();
                setIsScanningPages(false);

                // Move to bot selection step
                setTimeout(() => setCurrentStep('bot-selection'), 500);
            });

            // Handle errors
            eventSource.addEventListener('error', (e: any) => {
                try {
                    const data = JSON.parse(e.data);
                    toast.error(data.message || 'An error occurred during scanning');
                } catch {
                    toast.error('Connection error. Please try again.');
                }
                eventSource.close();
                setIsScanningPages(false);
                // Still move to bot selection even if scan fails
                setCurrentStep('bot-selection');
            });

            eventSource.onerror = (error) => {
                console.error('EventSource error:', error);
                eventSource.close();
                toast.error('Connection lost. Please try again.');
                setIsScanningPages(false);
                // Still move to bot selection even if scan fails
                setCurrentStep('bot-selection');
            };

        } catch (error) {
            console.error('Page scan error:', error);
            toast.error('Failed to start website scan');
            setIsScanningPages(false);
            // Still move to bot selection even if scan fails
            setCurrentStep('bot-selection');
        }
    };

    const clearDataSourcePolling = () => {
        if (dataSourcePollRef.current) {
            clearInterval(dataSourcePollRef.current);
            dataSourcePollRef.current = null;
        }
    };

    const pollDataSourceStatus = async () => {
        if (!clientId || !isMounted.current) return;
        try {
            const response = await fetchDataSourceStatus(clientId);
            const record = response?.data_source;
            if (!record) return;

            setDataSourceStatus(record);
            if (record?.source_type) {
                setSelectedDataSource(record.source_type);
            }
            if (record?.config?.last_sync_summary) {
                setDataSourceSyncSummary(record.config.last_sync_summary);
            }

            if (record?.status === 'error') {
                const message = record?.error_message || 'Sync failed. Please review your credentials.';
                setDataSourceSyncState('error');
                setDataSourceError(message);
                toast.error(message, { toastId: `datasource-error-${clientId}` });
                clearDataSourcePolling();
                setCurrentStep('data-source-credentials');
                postSyncTransitionRef.current = false;
                return;
            }

            if (record?.status === 'active') {
                setDataSourceSyncState('success');
                if (!postSyncTransitionRef.current) {
                    postSyncTransitionRef.current = true;
                    clearDataSourcePolling();
                    setTimeout(async () => {
                        if (!isMounted.current) return;
                        if (clientId && formData.website) {
                            setCurrentStep('page-scanning');
                            await scanWebsitePages(clientId, formData.website);
                        } else {
                            setCurrentStep('page-scanning');
                            setScanMessage('Product import completed successfully.');
                            setIsScanningPages(false);
                        }
                    }, 1500);
                }
                return;
            }

            setDataSourceSyncState('syncing');
        } catch (error: any) {
            console.error('[CLIENT-FORM] Failed to poll data source status:', error);
            setDataSourceSyncState('error');
            setDataSourceError(error?.response?.data?.detail || error.message || 'Unable to check sync status.');
            clearDataSourcePolling();
        }
    };

    const startDataSourcePolling = (triggerImmediate: boolean = true) => {
        if (!clientId) return;
        clearDataSourcePolling();
        if (triggerImmediate) {
            pollDataSourceStatus();
        }
        dataSourcePollRef.current = setInterval(() => {
            pollDataSourceStatus();
        }, 5000);
    };

    // Old streaming functions removed - now handled by DataSourceProgress component

    const handleManualDataSourceSync = async () => {
        if (!clientId || !selectedDataSource) {
            toast.error('Missing client ID or data source selection');
            return;
        }
        try {
            setDataSourceSyncState('syncing');
            setDataSourceError(null);
            setDataSourceSyncSummary(null);
            const response = await triggerDataSourceSync(clientId, selectedDataSource);
            if (response?.summary) {
                setDataSourceSyncSummary(response.summary);
            }
            toast.info('Sync requested. Watching for updates...');
            startDataSourcePolling(true);
        } catch (error: any) {
            console.error('[CLIENT-FORM] Failed to trigger data source sync', error);
            const message = error?.response?.data?.detail || error.message || 'Failed to trigger sync';
            setDataSourceSyncState('error');
            setDataSourceError(message);
            toast.error(message);
        }
    };

    const connectShopifyDataSource = async (config: any) => {
        if (!clientId) {
            const error = new Error('Client ID not available yet. Please create the client first.');
            toast.error(error.message);
            throw error;
        }

        const domain = (config?.shop_domain || '').trim();
        const token = (config?.access_token || '').trim();

        if (!domain || !token) {
            const error = new Error('Shop domain and access token are required');
            toast.error(error.message);
            throw error;
        }

        const normalizedDomain = domain
            .replace(/^https?:\/\//i, '')
            .split('/')[0]
            .toLowerCase();

        setSavingDataSource(true);
        setDataSourceError(null);
        setDataSourceSyncSummary(null);

        // Only change step if this is not from inline submission
        if (!config?.inline_submitted) {
            setDataSourceSyncState('syncing');
            postSyncTransitionRef.current = false;
            setCurrentStep('data-source-syncing');
        }

        try {
            const payload = {
                source_type: 'shopify_api',
                shop_domain: normalizedDomain,
                access_token: token,
                api_version: (config?.api_version || '').trim() || undefined,
                auto_sync_enabled: config?.auto_sync_enabled !== false,
                sync_frequency_hours: Number(config?.sync_frequency_hours) || 6,
                trigger_sync: true
            };

            const response = await configureDataSource(clientId, payload);
            setDataSourceStatus(response?.data_source || null);
            setDataSourceConfig(payload);
            setPendingDataSource(null);
            setSelectedDataSource('shopify_api');

            // Success - now change step and start SSE streaming
            setDataSourceSyncState('syncing');
            postSyncTransitionRef.current = false;
            setCurrentStep('data-source-syncing');

            toast.success('Shopify connection saved. Starting initial import...');
            // The DataSourceProgress component will handle streaming
        } catch (error: any) {
            console.error('[CLIENT-FORM] Failed to configure Shopify data source', error);
            const message = error?.response?.data?.detail || error.message || 'Failed to connect Shopify store';

            // Don't change step on error if inline submission
            if (!config?.inline_submitted) {
                setDataSourceError(message);
                setDataSourceSyncState('error');
                clearDataSourcePolling();
                setCurrentStep('data-source-credentials');
                postSyncTransitionRef.current = false;
            }

            toast.error(message);
            throw new Error(message);
        } finally {
            setSavingDataSource(false);
        }
    };

    const connectWooDataSource = async (config: any) => {
        if (!clientId) {
            const error = new Error('Client ID not available yet. Please create the client first.');
            toast.error(error.message);
            throw error;
        }

        const url = (config?.store_url || '').trim();
        const key = (config?.consumer_key || '').trim();
        const secret = (config?.consumer_secret || '').trim();

        if (!url || !key || !secret) {
            const error = new Error('Store URL, consumer key, and consumer secret are required');
            toast.error(error.message);
            throw error;
        }

        const normalizedUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;

        setSavingDataSource(true);
        setDataSourceError(null);
        setDataSourceSyncSummary(null);

        // Only change step if this is not from inline submission
        if (!config?.inline_submitted) {
            setDataSourceSyncState('syncing');
            postSyncTransitionRef.current = false;
            setCurrentStep('data-source-syncing');
        }

        try {
            const payload = {
                source_type: 'woocommerce_api',
                store_url: normalizedUrl,
                consumer_key: key,
                consumer_secret: secret,
                auto_sync_enabled: config?.auto_sync_enabled !== false,
                sync_frequency_hours: Number(config?.sync_frequency_hours) || 6,
                trigger_sync: true
            };

            const response = await configureDataSource(clientId, payload);
            setDataSourceStatus(response?.data_source || null);
            setDataSourceConfig(payload);
            setPendingDataSource(null);
            setSelectedDataSource('woocommerce_api');

            // Success - now change step and start polling
            setDataSourceSyncState('syncing');
            postSyncTransitionRef.current = false;
            setCurrentStep('data-source-syncing');

            toast.success('WooCommerce connection saved. Starting initial import...');
            // The DataSourceProgress component will handle streaming
        } catch (error: any) {
            console.error('[CLIENT-FORM] Failed to configure WooCommerce data source', error);
            const message = error?.response?.data?.detail || error.message || 'Failed to connect WooCommerce store';

            // Don't change step on error if inline submission
            if (!config?.inline_submitted) {
                setDataSourceError(message);
                setDataSourceSyncState('error');
                clearDataSourcePolling();
                setCurrentStep('data-source-credentials');
                postSyncTransitionRef.current = false;
            }

            toast.error(message);
            throw new Error(message);
        } finally {
            setSavingDataSource(false);
        }
    };

    const submitShopifyCredentials = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const domain = shopifyCredentials.shopDomain.trim();
        const token = shopifyCredentials.accessToken.trim();
        if (!domain || !token) {
            toast.error('Please provide both shop domain and access token');
            return;
        }

        const normalizedDomain = domain
            .replace(/^https?:\/\//, '')
            .split('/')[0]
            .toLowerCase();
        setShopifyCredentials(prev => ({ ...prev, shopDomain: normalizedDomain }));

        try {
            await connectShopifyDataSource({
                shop_domain: normalizedDomain,
                access_token: token,
                api_version: shopifyCredentials.apiVersion?.trim(),
                auto_sync_enabled: shopifyCredentials.autoSyncEnabled,
                sync_frequency_hours: shopifyCredentials.syncFrequencyHours
            });
            setPendingDataSource(null);
        } catch {
            // Errors already handled in helper
        }
    };

    const connectFeedDataSource = async (config: any) => {
        console.log('[connectFeedDataSource] Starting with config:', {
            config,
            clientId,
            currentStep
        });

        if (!clientId) {
            const error = new Error('Client ID not available yet. Please create the client first.');
            console.error('[connectFeedDataSource] No client ID available');
            toast.error(error.message);
            throw error;
        }

        const feedUrl = (config?.feed_url || '').trim();
        console.log('[connectFeedDataSource] Feed URL:', feedUrl);

        if (!feedUrl) {
            const error = new Error('Feed URL is required');
            console.error('[connectFeedDataSource] No feed URL provided');
            toast.error(error.message);
            throw error;
        }

        // Validate URL format
        try {
            new URL(feedUrl);
        } catch {
            const error = new Error('Please enter a valid feed URL');
            console.error('[connectFeedDataSource] Invalid feed URL format');
            toast.error(error.message);
            throw error;
        }

        console.log('[connectFeedDataSource] Setting up state for syncing');
        setSavingDataSource(true);
        setDataSourceError(null);
        setDataSourceSyncSummary(null);

        // Always set syncing state for feed (no inline submission concept for feed)
        console.log('[connectFeedDataSource] Transitioning to syncing step');
        setDataSourceSyncState('syncing');
        postSyncTransitionRef.current = false;
        setCurrentStep('data-source-syncing');

        try {
            const payload = {
                source_type: 'feed',
                feed_url: feedUrl,
                auto_sync_enabled: config?.auto_sync_enabled !== false,
                sync_frequency_hours: Number(config?.sync_frequency_hours) || 24,
                trigger_sync: true
            };

            console.log('[connectFeedDataSource] Calling configureDataSource with payload:', payload);
            const response = await configureDataSource(clientId, payload);
            console.log('[connectFeedDataSource] configureDataSource response:', response);

            setDataSourceStatus(response?.data_source || null);
            setDataSourceConfig(payload);
            setPendingDataSource(null);
            setSelectedDataSource('feed');

            // State already set above - no need to change again
            console.log('[connectFeedDataSource] Feed configured successfully, already in syncing state');
            toast.success('Feed configured. Starting product import...');
            // The DataSourceProgress component will handle streaming
        } catch (error: any) {
            console.error('[connectFeedDataSource] Failed to configure feed data source:', error);
            const message = error?.response?.data?.detail || error.message || 'Failed to configure feed';

            // On error, show error state
            setDataSourceError(message);
            setDataSourceSyncState('error');
            clearDataSourcePolling();

            // Go back to credentials step to show error
            console.log('[connectFeedDataSource] Error occurred, going to credentials step to show error');
            setCurrentStep('data-source-credentials');
            postSyncTransitionRef.current = false;

            toast.error(message);
            throw new Error(message);
        } finally {
            setSavingDataSource(false);
        }
    };

    const submitFeedCredentials = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const feedUrl = feedCredentials.feedUrl.trim();

        if (!feedUrl) {
            toast.error('Please provide a feed URL');
            return;
        }

        try {
            await connectFeedDataSource({
                feed_url: feedUrl,
                auto_sync_enabled: feedCredentials.autoSyncEnabled,
                sync_frequency_hours: feedCredentials.syncFrequencyHours
            });
            setPendingDataSource(null);
        } catch {
            // Errors already handled in helper
        }
    };

    const submitWooCredentials = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const url = wooCredentials.storeUrl.trim();
        const key = wooCredentials.consumerKey.trim();
        const secret = wooCredentials.consumerSecret.trim();
        if (!url || !key || !secret) {
            toast.error('Please provide store URL, consumer key, and consumer secret');
            return;
        }

        const normalizedUrl = url.startsWith('http://') || url.startsWith('https://')
            ? url
            : `https://${url}`;
        setWooCredentials(prev => ({ ...prev, storeUrl: normalizedUrl }));

        try {
            await connectWooDataSource({
                store_url: normalizedUrl,
                consumer_key: key,
                consumer_secret: secret,
                auto_sync_enabled: wooCredentials.autoSyncEnabled,
                sync_frequency_hours: wooCredentials.syncFrequencyHours
            });
            setPendingDataSource(null);
        } catch {
            // Errors already handled in helper
        }
    };

    // Removed unused handlePreScan function

    const handleStartCrawl = async () => {
        if (!clientId || !formData.website.trim()) {
            toast.error('Missing client ID or website URL');
            return;
        }

        setLoading(true);
        setCurrentStep('crawling'); // Move to crawling step

        try {
            // Clear any existing "no crawl" flag from sessionStorage
            const noCrawlKey = `no_crawl_${clientId}`;
            sessionStorage.removeItem(noCrawlKey);

            // First, update the client's personality
            const personalityResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/client/${clientId}/personality`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                },
                body: JSON.stringify({
                    personality: botPersonality
                })
            });

            if (!personalityResponse.ok) {
                const error = await personalityResponse.json();
                console.error('Failed to update personality:', error);
                // Continue anyway, personality will default to professional
            }

            // Start the crawl with bot purpose and custom prompt
            const truncatedSelectedPages = preScannedPages.length > 0 ? Array.from(selectedPages).slice(0, MAX_PAGE_SELECTION) : null;

            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/rescrap/${clientId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                },
                body: JSON.stringify({
                    url: formData.website,
                    scraping_method: 'firecrawl',
                    bot_purpose: botPurpose,
                    bot_type: botPurpose,  // Pass bot_type (same as bot_purpose for now)
                    bot_personality: botPersonality,
                    // Only send custom prompt if it was shown and modified from default
                    custom_prompt: showPromptEditor && customPrompt !== BOT_PURPOSE_PROMPTS[botPurpose].prompt ? customPrompt : null,
                    skip_deletion_and_reuse: false,
                    selected_pages: truncatedSelectedPages  // Only send if we have scanned pages
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to start crawl');
            }

            const result = await response.json();
            console.log('[CLIENT-FORM] Crawl started:', result);

            // Add crawl job to global tracking
            const jobId = result.job_id || `consultant-crawl-${clientId}-${Date.now()}`;
            const selectedCount = truncatedSelectedPages ? truncatedSelectedPages.length : (selectedPages.size || 1);
            const cappedSelectedCount = Math.min(selectedCount || 1, MAX_PAGE_SELECTION);
            addCrawlJob({
                id: jobId,
                clientId: clientId,
                url: formData.website,
                status: 'initializing',
                pages_crawled: 0,
                pages_in_queue: cappedSelectedCount || 1,
                max_pages: cappedSelectedCount,
                progress_percentage: 0,
                start_time: new Date().toISOString()
            });

            toast.success('Crawl started! Monitoring progress...');
            setLoading(false);
            
            // Navigate to progress page AFTER crawl successfully starts
            navigate(`/crawl-details/${clientId}`);

        } catch (error: any) {
            console.error('Error starting crawl:', error);
            toast.error(error.message || 'Failed to start website crawl');
            setLoading(false);
            setCurrentStep('bot-selection');
            setShowProgress(false);
        }
    };

    // Add the missing handleSubmit function
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Check if we have pages selected
        if (selectedPages.size === 0 && preScannedPages.length > 0) {
            toast.warning('Please select at least one page to crawl');
            setCurrentStep('page-selection');
            return;
        }
        
        await handleStartCrawl();
    };

    // Toggle page selection
    const togglePageSelection = (url: string) => {
        setHasManualSelection(true);
        const newSelection = new Set(selectedPages);
        if (newSelection.has(url)) {
            newSelection.delete(url);
        } else {
            newSelection.add(url);
        }
        setSelectedPages(newSelection);
    };

    // Select/deselect all pages (now works with filtered pages)
    const toggleSelectAll = () => {
        const { limitedPages } = getBotSelectionContext(botPurpose);
        const limitedSet = new Set(limitedPages.map((page: any) => page.url));
        const allSelected =
            limitedSet.size > 0 &&
            limitedSet.size === selectedPages.size &&
            Array.from(limitedSet).every(url => selectedPages.has(url));

        if (allSelected) {
            setHasManualSelection(true);
            setSelectedPages(new Set());
        } else {
            setHasManualSelection(false);
            setSelectedPages(limitedSet);
        }
    };

    // Handle stop crawl
    const handleStopCrawl = async () => {
        if (!clientId) return;
        
        setIsStoppingCrawl(true);
        try {
            await stopCrawl(clientId);
            toast.success('Crawl stopped. Processing collected data...');
        } catch (error) {
            console.error('Error stopping crawl:', error);
            toast.error('Failed to stop crawl');
        } finally {
            setIsStoppingCrawl(false);
        }
    };

    // Handle abort crawl
    const handleAbortCrawl = async () => {
        if (!clientId) return;
        
        if (!window.confirm('Are you sure you want to abort? All crawled data will be lost.')) {
            return;
        }
        
        setIsAbortingCrawl(true);
        try {
            await abortCrawl(clientId);
            toast.info('Crawl aborted');
            navigate('/consultant/dashboard');
        } catch (error) {
            console.error('Error aborting crawl:', error);
            toast.error('Failed to abort crawl');
        } finally {
            setIsAbortingCrawl(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 py-8">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Simplified Current Step Indicator */}
                {currentStep !== 'crawling' && (
                    <div className="mb-6">
                        <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-center gap-2 text-sm">
                                <span className="text-gray-600">Current Step:</span>
                                <span className="font-semibold text-blue-600">
                                    {currentStep === 'client-details' ? '1. Client Setup' :
                                     ['data-source-selection', 'data-source-credentials', 'data-source-syncing'].includes(currentStep) ? '2. Data Source Configuration' :
                                     currentStep === 'page-scanning' ? '3. Website Analysis' :
                                     currentStep === 'bot-selection' ? '4. Bot Configuration' :
                                     currentStep === 'page-selection' ? '5. Page Selection' : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content Area */}
                <div className="transition-all duration-500 ease-in-out">
                    {currentStep === 'client-details' ? (
                        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                        
                        {hasActiveCrawl && (
                                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 mb-6">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                                                <FontAwesomeIcon icon={faSpider} className="text-amber-600 text-lg" />
                                            </div>
                                        </div>
                                        <div className="ml-4">
                                            <h3 className="text-sm font-semibold text-amber-800 mb-1">Active Crawl in Progress</h3>
                                            <p className="text-sm text-amber-700">
                                                A website crawl is currently running. Please wait for it to complete.
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/crawl-details/${activeCrawlClientId}`)}
                                                className="mt-3 inline-flex items-center text-sm font-medium text-amber-800 hover:text-amber-900 transition-colors"
                                            >
                                                View active crawl
                                                <FontAwesomeIcon icon={faArrowRight} className="ml-2 text-xs" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                        )}

                        {error && (
                                <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-5 mb-6">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-lg" />
                                            </div>
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm text-red-800">{error}</p>
                                        </div>
                                    </div>
                                </div>
                        )}

                            {/* Section Header */}
                            <div className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">Client Information</h2>
                                <p className="text-gray-600">Enter your client's details to create their AI assistant</p>
                            </div>

                            <form onSubmit={handleClientDetailsSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Name Field */}
                                    <div className="group">
                                        <label htmlFor="name" className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
                                                <FontAwesomeIcon icon={faUser} className="text-blue-600 text-sm" />
                                            </div>
                                            Full Name
                                            <span className="text-red-500 ml-1">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            required
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            placeholder="John Doe"
                                        />
                                    </div>

                                    {/* Display Name Field */}
                                    <div className="group">
                                        <label htmlFor="prompt_display_name" className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
                                                <FontAwesomeIcon icon={faBuilding} className="text-blue-600 text-sm" />
                                            </div>
                                            Organization Display Name
                                        </label>
                                        <input
                                            type="text"
                                            id="prompt_display_name"
                                            name="prompt_display_name"
                                            value={formData.prompt_display_name}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            placeholder="ACME Corp"
                                        />
                                        <p className="mt-2 text-xs text-gray-500 flex items-start">
                                            <FontAwesomeIcon icon={faInfoCircle} className="mr-1 mt-0.5 text-gray-400" />
                                            How the organization appears in AI responses. Leave blank to use full name.
                                        </p>
                                    </div>

                                    {/* Email Field */}
                                    <div className="group">
                                        <label htmlFor="email" className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
                                                <FontAwesomeIcon icon={faEnvelope} className="text-blue-600 text-sm" />
                                            </div>
                                            Email Address
                                            <span className="text-red-500 ml-1">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            placeholder="client@example.com"
                                        />
                                    </div>

                                    {/* Phone Field */}
                                    <div className="group">
                                        <label htmlFor="phone" className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
                                                <FontAwesomeIcon icon={faPhone} className="text-blue-600 text-sm" />
                                            </div>
                                            Phone Number
                                            <span className="text-red-500 ml-1">*</span>
                                        </label>
                                        <input
                                            type="tel"
                                            pattern="[0-9+]+"
                                            onKeyPress={(e) => {
                                                if (!/[0-9+]/.test(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            id="phone"
                                            name="phone"
                                            required
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            placeholder="+1 (555) 123-4567"
                                        />
                                    </div>

                                    {/* Website Field */}
                                    <div className="group">
                                        <label htmlFor="website" className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
                                                <FontAwesomeIcon icon={faGlobe} className="text-blue-600 text-sm" />
                                            </div>
                                            Website URL
                                        </label>
                                        <input
                                            type="url"
                                            id="website"
                                            name="website"
                                            value={formData.website}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            placeholder="https://example.com"
                                        />
                                        <p className="mt-2 text-xs text-gray-500 flex items-start">
                                            <FontAwesomeIcon icon={faInfoCircle} className="mr-1 mt-0.5 text-gray-400" />
                                            We'll intelligently crawl pages based on the bot type you select.
                                        </p>
                                    </div>

                                    {/* Personality Field */}
                                    <div className="group">
                                        <label htmlFor="personality" className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
                                                <FontAwesomeIcon icon={faRobot} className="text-blue-600 text-sm" />
                                            </div>
                                            Bot Personality
                                            <span className="text-red-500 ml-1">*</span>
                                        </label>
                                        <select
                                            id="personality"
                                            name="personality"
                                            required
                                            value={botPersonality}
                                            onChange={(e) => setBotPersonality(e.target.value)}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        >
                                            {Object.entries(BOT_PERSONALITIES).map(([key, personality]) => (
                                                <option key={key} value={key}>
                                                    {personality.name} - {personality.description}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="mt-2 text-xs text-gray-500 flex items-start">
                                            <FontAwesomeIcon icon={faInfoCircle} className="mr-1 mt-0.5 text-gray-400" />
                                            Defines how your AI assistant will communicate with users.
                                        </p>
                                    </div>

                                    {/* Password Field */}
                                    <div className="group">
                                        <label htmlFor="password" className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
                                                <FontAwesomeIcon icon={faLock} className="text-blue-600 text-sm" />
                                            </div>
                                            Password
                                            <span className="text-red-500 ml-1">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={passwordVisible ? "text" : "password"}
                                                id="password"
                                                name="password"
                                                required
                                                value={formData.password}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-12"
                                                placeholder="Create a secure password"
                                            />
                                            <button
                                                type="button"
                                                onClick={togglePasswordVisibility}
                                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                            >
                                                <FontAwesomeIcon icon={passwordVisible ? faEyeSlash : faEye} />
                                            </button>
                                        </div>
                                        <p className="mt-2 text-xs text-gray-500 flex items-start">
                                            <FontAwesomeIcon icon={faInfoCircle} className="mr-1 mt-0.5 text-gray-400" />
                                            Minimum 8 characters with letters and numbers.
                                        </p>
                                    </div>
                            </div>

                                {/* Action Buttons */}
                                <div className="flex justify-between items-center pt-8 mt-8 border-t border-gray-100">
                                    <button
                                        type="button"
                                        onClick={() => navigate('/consultant/dashboard')}
                                        className="flex items-center text-gray-600 hover:text-gray-800 font-medium transition-colors"
                                    >
                                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                                        Back to Dashboard
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading || hasActiveCrawl}
                                        className={`
                                            flex items-center px-8 py-3.5 rounded-xl font-semibold text-white
                                            transition-all duration-200 transform
                                            ${loading || hasActiveCrawl 
                                                ? 'bg-gray-400 cursor-not-allowed' 
                                                : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 hover:scale-105 shadow-lg hover:shadow-xl'
                                            }
                                        `}
                                    >
                                        {loading ? (
                                            <>
                                                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                Continue
                                                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : currentStep === 'data-source-selection' ? (
                        // Data Source Selection Step
                        <DataSourceSelector
                            websiteUrl={formData.website}
                            onMethodSelected={handleDataSourceSelected}
                            onSkip={handleSkipDataSourceSelection}
                            autoDetect={true}
                            className="animate-fade-in"
                        />
                    ) : currentStep === 'data-source-credentials' ? (
                        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 animate-fade-in">
                            {console.log('[CLIENT-FORM] Rendering data-source-credentials, pendingDataSource:', pendingDataSource)}
                            <div className="flex items-start justify-between mb-8">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900 mb-1">
                                        {pendingDataSource?.method === 'shopify_api' ? 'Connect Shopify Store' :
                                         pendingDataSource?.method === 'woocommerce_api' ? 'Connect WooCommerce Store' :
                                         pendingDataSource?.method === 'feed' ? 'Configure Product Feed' : 'Connect Data Source'}
                                    </h2>
                                    <p className="text-gray-600">
                                        {pendingDataSource?.method === 'feed'
                                            ? 'Configure your product feed URL for automatic imports. Supports XML and JSON feeds.'
                                            : 'Securely connect your store to import products directly from the source. Credentials are stored encrypted.'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        clearDataSourcePolling();
                                        setPendingDataSource(null);
                                        setCurrentStep('data-source-selection');
                                    }}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    Change Method
                                </button>
                            </div>

                            {(pendingDataSource?.method === 'shopify_api') ? (
                                <form onSubmit={submitShopifyCredentials} className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Shopify Domain
                                        </label>
                                        <input
                                            type="text"
                                            value={shopifyCredentials.shopDomain}
                                            onChange={(e) => setShopifyCredentials(prev => ({ ...prev, shopDomain: e.target.value }))}
                                            placeholder="your-store.myshopify.com"
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                            required
                                        />
                                        <p className="mt-2 text-xs text-gray-500">
                                            Use your Shopify primary domain (without https://). Custom domains are supported.
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Admin Access Token
                                        </label>
                                        <input
                                            type="password"
                                            value={shopifyCredentials.accessToken}
                                            onChange={(e) => setShopifyCredentials(prev => ({ ...prev, accessToken: e.target.value }))}
                                            placeholder="shpat_..."
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                            required
                                        />
                                        <p className="mt-2 text-xs text-gray-500">
                                            Create a private app in Shopify (Settings → Apps and sales channels → Develop apps) and copy the Admin API token.
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                API Version (optional)
                                            </label>
                                            <input
                                                type="text"
                                                value={shopifyCredentials.apiVersion}
                                                onChange={(e) => setShopifyCredentials(prev => ({ ...prev, apiVersion: e.target.value }))}
                                                placeholder="2024-01"
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Auto Sync Frequency (hours)
                                            </label>
                                            <input
                                                type="number"
                                                min={1}
                                                value={shopifyCredentials.syncFrequencyHours}
                                                onChange={(e) => setShopifyCredentials(prev => ({ ...prev, syncFrequencyHours: Number(e.target.value) }))}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-3">
                                        <input
                                            id="shopify_auto_sync"
                                            type="checkbox"
                                            checked={shopifyCredentials.autoSyncEnabled}
                                            onChange={(e) => setShopifyCredentials(prev => ({ ...prev, autoSyncEnabled: e.target.checked }))}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor="shopify_auto_sync" className="text-sm text-gray-700">
                                            Enable automatic re-sync at the selected interval
                                        </label>
                                    </div>

                                    {dataSourceError && (
                                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                                            {dataSourceError}
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between pt-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                clearDataSourcePolling();
                                                setPendingDataSource(null);
                                                setCurrentStep('data-source-selection');
                                            }}
                                            className="px-4 py-2 rounded-lg text-gray-600 hover:text-gray-800 transition-colors"
                                        >
                                            Back
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={savingDataSource}
                                            className={`px-6 py-3 rounded-xl font-semibold text-white shadow-lg transition-all ${
                                                savingDataSource
                                                    ? 'bg-gray-400 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
                                            }`}
                                        >
                                            {savingDataSource ? (
                                                <>
                                                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                                    Connecting...
                                                </>
                                            ) : (
                                                'Connect & Import'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            ) : pendingDataSource?.method === 'woocommerce_api' ? (
                                <form onSubmit={submitWooCredentials} className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Store URL
                                        </label>
                                        <input
                                            type="text"
                                            value={wooCredentials.storeUrl}
                                            onChange={(e) => setWooCredentials(prev => ({ ...prev, storeUrl: e.target.value }))}
                                            placeholder="https://yourstore.com"
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                            required
                                        />
                                        <p className="mt-2 text-xs text-gray-500">
                                            Include https://. WooCommerce REST API must be enabled (Settings → Advanced → REST API).
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Consumer Key
                                            </label>
                                            <input
                                                type="text"
                                                value={wooCredentials.consumerKey}
                                                onChange={(e) => setWooCredentials(prev => ({ ...prev, consumerKey: e.target.value }))}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Consumer Secret
                                            </label>
                                            <input
                                                type="password"
                                                value={wooCredentials.consumerSecret}
                                                onChange={(e) => setWooCredentials(prev => ({ ...prev, consumerSecret: e.target.value }))}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Auto Sync Frequency (hours)
                                            </label>
                                            <input
                                                type="number"
                                                min={1}
                                                value={wooCredentials.syncFrequencyHours}
                                                onChange={(e) => setWooCredentials(prev => ({ ...prev, syncFrequencyHours: Number(e.target.value) }))}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                            />
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <input
                                                id="woo_auto_sync"
                                                type="checkbox"
                                                checked={wooCredentials.autoSyncEnabled}
                                                onChange={(e) => setWooCredentials(prev => ({ ...prev, autoSyncEnabled: e.target.checked }))}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="woo_auto_sync" className="text-sm text-gray-700">
                                                Enable automatic re-sync at the selected interval
                                            </label>
                                        </div>
                                    </div>

                                    {dataSourceError && (
                                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                                            {dataSourceError}
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between pt-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setPendingDataSource(null);
                                                setCurrentStep('data-source-selection');
                                            }}
                                            className="px-4 py-2 rounded-lg text-gray-600 hover:text-gray-800 transition-colors"
                                        >
                                            Back
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={savingDataSource}
                                            className={`px-6 py-3 rounded-xl font-semibold text-white shadow-lg transition-all ${
                                                savingDataSource
                                                    ? 'bg-gray-400 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
                                            }`}
                                        >
                                            {savingDataSource ? (
                                                <>
                                                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                                    Connecting...
                                                </>
                                            ) : (
                                                'Connect & Import'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            ) : pendingDataSource?.method === 'feed' ? (
                                <form onSubmit={submitFeedCredentials} className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Feed URL
                                        </label>
                                        <input
                                            type="url"
                                            value={feedCredentials.feedUrl}
                                            onChange={(e) => setFeedCredentials(prev => ({ ...prev, feedUrl: e.target.value }))}
                                            placeholder="https://storage.feedyio.com/your-feed.xml"
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                            required
                                        />
                                        <p className="mt-2 text-xs text-gray-500">
                                            Enter the full URL to your product feed (XML or JSON format).
                                        </p>
                                    </div>

                                    <div>
                                        <label className="flex items-center space-x-3">
                                            <input
                                                type="checkbox"
                                                checked={feedCredentials.autoSyncEnabled}
                                                onChange={(e) => setFeedCredentials(prev => ({
                                                    ...prev,
                                                    autoSyncEnabled: e.target.checked
                                                }))}
                                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-sm font-medium text-gray-700">
                                                Enable automatic sync
                                            </span>
                                        </label>
                                        <p className="ml-7 mt-1 text-xs text-gray-500">
                                            Automatically fetch and update products from your feed.
                                        </p>
                                    </div>

                                    {feedCredentials.autoSyncEnabled && (
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Sync Frequency
                                            </label>
                                            <select
                                                value={feedCredentials.syncFrequencyHours}
                                                onChange={(e) => setFeedCredentials(prev => ({
                                                    ...prev,
                                                    syncFrequencyHours: Number(e.target.value)
                                                }))}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                            >
                                                <option value="6">Every 6 hours</option>
                                                <option value="12">Every 12 hours</option>
                                                <option value="24">Once daily</option>
                                                <option value="48">Every 2 days</option>
                                                <option value="168">Once weekly</option>
                                            </select>
                                            <p className="mt-2 text-xs text-gray-500">
                                                How often to check for product updates in your feed.
                                            </p>
                                        </div>
                                    )}

                                    {dataSourceError && (
                                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                                            {dataSourceError}
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between pt-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setPendingDataSource(null);
                                                setCurrentStep('data-source-selection');
                                            }}
                                            className="px-4 py-2 rounded-lg text-gray-600 hover:text-gray-800 transition-colors"
                                        >
                                            Back
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={savingDataSource}
                                            className={`px-6 py-3 rounded-xl font-semibold text-white shadow-lg transition-all ${
                                                savingDataSource
                                                    ? 'bg-gray-400 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
                                            }`}
                                        >
                                            {savingDataSource ? (
                                                <>
                                                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                                    Configuring...
                                                </>
                                            ) : (
                                                'Configure & Import'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            ) : null}
                        </div>
                    ) : currentStep === 'data-source-syncing' ? (
                        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 animate-fade-in">
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">
                                        Importing Products
                                    </h2>
                                    <p className="text-gray-600">
                                        Products are being imported and embedded into the knowledge base.
                                    </p>
                                </div>
                                <div className="space-x-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setCurrentStep('data-source-credentials');
                                        }}
                                        className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
                                    >
                                        Edit Credentials
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setPendingDataSource(null);
                                            setCurrentStep('data-source-selection');
                                        }}
                                        className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                        Change Method
                                    </button>
                                </div>
                            </div>

                            {/* Use the new DataSourceProgress component */}
                            <DataSourceProgress
                                clientId={clientId}
                                sourceType={selectedDataSource as 'shopify_api' | 'woocommerce_api' | 'feed'}
                                onComplete={(success, summary) => {
                                    if (success) {
                                        console.log('[CLIENT-FORM] Data source sync completed successfully', summary);
                                        setDataSourceSyncState('success');
                                        setDataSourceSyncSummary(summary);

                                        // For Shopify/WooCommerce/Feed - go directly to dashboard
                                        // No bot selection or crawling needed - that's only for web scraping
                                        toast.success(`Successfully imported ${summary?.processed_products || 0} products! Redirecting to dashboard...`);

                                        setTimeout(() => {
                                            navigate('/consultant/dashboard');
                                        }, 2000);
                                    }
                                }}
                                onError={(error) => {
                                    console.error('[CLIENT-FORM] Data source sync error:', error);
                                    setDataSourceSyncState('error');
                                    setDataSourceError(error);
                                    toast.error(error);
                                }}
                            />

                            <div className="mt-10 flex items-center justify-between">
                                <div className="space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setCurrentStep('data-source-credentials');
                                        }}
                                        className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                                    >
                                        Update Credentials
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleManualDataSourceSync}
                                    disabled={dataSourceSyncState === 'syncing'}
                                    className={`px-5 py-2.5 rounded-lg font-semibold text-white transition-all ${
                                        dataSourceSyncState === 'syncing'
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
                                    }`}
                                >
                                    {dataSourceSyncState === 'syncing' ? 'Sync in Progress' : 'Re-sync Now'}
                                </button>
                            </div>
                        </div>
                    ) : currentStep === 'page-scanning' ? (
                        // Enhanced Page Scanning UI - Matches Demo
                        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                            <div className="rounded-xl border border-gray-200 p-6 bg-white">
                                <div className="space-y-4">
                                    {/* Progress bar */}
                                    <div className="rounded-full h-2 overflow-hidden bg-gray-100">
                                        <div
                                            className="bg-gradient-to-r from-blue-500 to-indigo-500 h-full transition-all duration-300"
                                            style={{ width: `${scanProgress}%` }}
                                        />
                                    </div>

                                    {/* Status message */}
                                    <p className="text-center text-sm text-gray-600">
                                        {scanMessage || 'Discovering pages...'}
                                    </p>

                                    {/* Beautiful animated loading dots */}
                                    {isScanningPages && (
                                        <div className="flex justify-center items-center space-x-2 mt-4">
                                            <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                            <div className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                            <div className="w-3 h-3 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                        </div>
                                    )}

                                    {/* Live page discovery feed */}
                                    {discoveredPages.length > 0 && (
                                        <div className="mt-4 border-t pt-4">
                                            <p className="text-xs text-gray-500 mb-2">
                                                Discovered pages ({discoveredPages.length}):
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                                                {discoveredPages.map((page: any, idx: number) => (
                                                    <div key={idx} className="flex items-center space-x-2 text-sm animate-fade-in p-2 rounded bg-white shadow-sm border">
                                                        <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 flex-shrink-0" />
                                                        <a
                                                            href={page.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="truncate flex-1 text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200"
                                                            title={`Visit: ${page.url}`}
                                                        >
                                                            {page.title || page.url || 'Page'}
                                                        </a>
                                                        {page.category && (
                                                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                                                {page.category}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : currentStep === 'bot-selection' ? (
                        // Enhanced Bot Purpose Selection
                        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                            <div className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">Configure AI Assistant</h2>
                                <p className="text-gray-600">Choose the type of AI assistant for your client</p>
                            </div>

                            {/* Categories Display - Show discovered categories */}
                            {aggregatedCategories.length > 0 && (
                                <div className="mb-8">
                                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
                                        <div className="text-center mb-4">
                                            <FontAwesomeIcon icon={faCheck} className="text-3xl text-green-500 mb-2" />
                                            <h3 className="text-lg font-bold text-gray-800">
                                                Found {totalDiscoveredPages || preScannedPages.length} pages
                                                {totalDiscoveredPages > totalAnalyzedPages && totalAnalyzedPages > 0 && (
                                                    <span className="text-base text-gray-600"> (analyzed {totalAnalyzedPages})</span>
                                                )} across {aggregatedCategories.length} {aggregatedCategories.length === 1 ? 'category' : 'categories'}
                                            </h3>
                                            {llmAnalysis && llmAnalysis.site_type && (
                                                <p className="text-sm text-gray-600 mt-1">
                                                    {llmAnalysis.site_type} • {llmAnalysis.analysis || 'Website content analyzed'}
                                                </p>
                                            )}
                                            {discoverySummary && (
                                                <div className="flex flex-wrap justify-center gap-2 mt-3 text-xs sm:text-sm text-gray-600">
                                                    {formatStatNumber(discoverySummary.unique_discovered) && (
                                                        <span className="px-3 py-1 bg-white border border-gray-200 rounded-full shadow-sm">
                                                            {formatStatNumber(discoverySummary.unique_discovered)} unique pages mapped
                                                        </span>
                                                    )}
                                                    {typeof discoverySummary.duplicates_skipped === 'number' && discoverySummary.duplicates_skipped > 0 && (
                                                        <span className="px-3 py-1 bg-white border border-gray-200 rounded-full shadow-sm">
                                                            Removed {formatStatNumber(discoverySummary.duplicates_skipped)} duplicates
                                                        </span>
                                                    )}
                                                    {formatStatNumber(discoverySummary.preview_pages_total) && (
                                                        <span className="px-3 py-1 bg-white border border-gray-200 rounded-full shadow-sm">
                                                            {formatStatNumber(discoverySummary.preview_pages_total)} ready for review
                                                        </span>
                                                    )}
                                                    {formatStatNumber(discoverySummary.filtered_for_bot) && discoverySummary.filtered_for_bot !== discoverySummary.preview_pages_total && (
                                                        <span className="px-3 py-1 bg-white border border-gray-200 rounded-full shadow-sm">
                                                            {formatStatNumber(discoverySummary.filtered_for_bot)} match selected bot
                                                        </span>
                                                    )}
                                                    {formatStatNumber(discoverySummary.max_preview_limit) && (
                                                        <span className="px-3 py-1 bg-white border border-gray-200 rounded-full shadow-sm">
                                                            Limit: {formatStatNumber(discoverySummary.max_preview_limit)} URLs
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                            <div className="flex flex-wrap gap-2">
                                                {aggregatedCategories.map((cat: AggregatedCategory) => {
                                                    const pagesForCategory = categoryPagesMap.get(cat.name) || [];
                                                    return (
                                                        <InlineCategoryCard
                                                            key={cat.name}
                                                            categoryName={cat.name}
                                                            icon={cat.icon}
                                                            count={cat.count}
                                                            listingCount={cat.listingCount}
                                                            listingChildrenCount={cat.listingChildrenCount}
                                                            sampleProducts={cat.sampleProductUrls}
                                                            pages={pagesForCategory}
                                                            onOpenTooltip={() => setActiveCategoryTooltip(cat.name)}
                                                            onCloseTooltip={() => setActiveCategoryTooltip(null)}
                                                            isTooltipOpen={activeCategoryTooltip === cat.name}
                                                            isSelected={false}
                                                            onSelect={() => {}}
                                                            hideSelectionCheckbox={true}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}


                            <div className="mb-8">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                    {botRecommendations.length > 0 ? 'AI-Recommended Bot Types' : 'Select Bot Purpose'}
                                </h3>
                                <div className="mb-6">
                                    <BotPurposeSelector
                                        selectedPurpose={botPurpose}
                                        onPurposeChange={setBotPurpose}
                                        disabled={loading}
                                        llmRecommendations={botRecommendations}
                                        recommendedType={recommendedBotType}
                                    />
                                </div>
                            </div>


                            {/* Action Buttons */}
                            <div className="flex justify-between items-center pt-8 mt-8 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep('client-details')}
                                    className="flex items-center text-gray-600 hover:text-gray-800 font-medium transition-colors"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                                    Back
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep('page-selection')}
                                    disabled={loading || hasActiveCrawl}
                                    className={`
                                        flex items-center px-8 py-3.5 rounded-xl font-semibold text-white
                                        transition-all duration-200 transform
                                        ${loading || hasActiveCrawl 
                                            ? 'bg-gray-400 cursor-not-allowed' 
                                            : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 hover:scale-105 shadow-lg hover:shadow-xl'
                                        }
                                    `}
                                >
                                    {loading ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                            Starting...
                                        </>
                                    ) : (
                                        <>
                                            {preScannedPages.length > 0 ? 'Select Pages' : 'Start Crawling'}
                                            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    ) : currentStep === 'page-selection' ? (
                        // Enhanced Page Selection
                        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                            <div className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Pages to Crawl</h2>
                                <p className="text-gray-600">Choose which pages to include in your AI's knowledge base</p>

                                {/* Debug info for bot recommendations */}
                                {/* {process.env.NODE_ENV === 'development' && (
                                    <div className="mb-4 p-3 bg-yellow-50 rounded-lg text-xs">
                                        <p>Debug: botRecommendations length: {botRecommendations.length}</p>
                                        <p>Debug: botPurpose: {botPurpose}</p>
                                        <p>Debug: preScannedPages length: {preScannedPages.length}</p>
                                        <p>Debug: selectedPages size: {selectedPages.size}</p>
                                    </div>
                                )} */}
                            </div>

                            {/* Filter pages based on selected bot type */}
                            {(() => {
                                const {
                                    selectedBotRec,
                                    botCategories,
                                    limitedPages,
                                    filterApplied,
                                    noCategoryMatches,
                                    effectiveTotal,
                                    shouldFilterByCategories
                                } = getBotSelectionContext(botPurpose);

                                return (
                                    <>
                                        {/* Selection Summary */}
                                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 mb-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h3 className="text-lg font-semibold text-gray-900">
                                                        {selectedPages.size} of {effectiveTotal} Pages Selected
                                                        {shouldFilterByCategories && botCategories.length > 0 && (
                                                            <span className="text-sm text-blue-600 ml-2">
                                                                (for {botPurpose})
                                                            </span>
                                                        )}
                                                    </h3>
                                                    {filterApplied && (
                                                        <p className="text-sm text-green-700 mt-1 font-medium">
                                                            ✅ Bot type filtering applied - showing only relevant pages
                                                        </p>
                                                    )}
                                                    {noCategoryMatches && (
                                                        <p className="text-sm text-amber-700 mt-1 font-medium">
                                                            ⚠️ No pages matched the selected bot categories - showing all pages instead
                                                        </p>
                                                    )}
                                                    {!noCategoryMatches && (!selectedBotRec || botCategories.length === 0) && (
                                                        <p className="text-sm text-amber-700 mt-1 font-medium">
                                                            ⚠️ No bot type recommendations found - showing all pages
                                                        </p>
                                                    )}
                                                    {pagesWithCategories.length === 0 && (
                                                        <p className="text-sm text-amber-700 mt-1 font-medium">
                                                            ⚠️ Pages don't have categories - showing all pages
                                                        </p>
                                                    )}
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        The AI will learn from the selected pages to answer questions
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={toggleSelectAll}
                                                    className="px-4 py-2 bg-white border border-blue-200 rounded-lg text-sm font-medium text-blue-700 hover:bg-blue-50 transition-colors"
                                                >
                                                    {(() => {
                                                        if (!limitedPages.length) return 'Select All';
                                                        const limitedAllSelected =
                                                            selectedPages.size === limitedPages.length &&
                                                            limitedPages.every(page => selectedPages.has(page.url));
                                                        return limitedAllSelected ? 'Deselect All' : 'Select All';
                                                    })()}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Pages List with Better Styling */}
                                        <div className="border border-gray-200 rounded-xl p-2 max-h-[400px] overflow-y-auto mb-6">
                                            <div className="space-y-2">
                                                {limitedPages.map((page: any, index: number) => (
                                                    <label
                                                        key={index}
                                                        className="flex items-start p-4 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors group"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedPages.has(page.url)}
                                                            onChange={() => togglePageSelection(page.url)}
                                                            className="w-5 h-5 text-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 mt-1 mr-4"
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-start justify-between">
                                                                <div className="flex-1 pr-2">
                                                                    <a
                                                                        href={page.url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200"
                                                                        title={`Visit: ${page.url}`}
                                                                    >
                                                                        {page.title || 'Untitled Page'}
                                                                    </a>
                                                                    <p className="text-sm text-gray-500 mt-1 truncate">
                                                                        {page.url}
                                                                    </p>
                                                                </div>
                                                                {page.category && (
                                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                                                        {page.category}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Show filtered pages info */}
                                        {shouldFilterByCategories && botCategories.length > 0 && (
                                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-blue-900">
                                                Showing pages for: <span className="font-semibold">{botPurpose}</span>
                                            </p>
                                            <p className="text-xs text-blue-700 mt-1">
                                                Filtered from {preScannedPages.length} total pages • {limitedPages.length} pages in selected categories
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setCurrentStep('bot-selection')}
                                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                                        >
                                            Change Bot Type
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    );
                })()}

                {/* Action Buttons */}
                            <div className="flex justify-between items-center pt-8 mt-8 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep('bot-selection')}
                                    className="flex items-center text-gray-600 hover:text-gray-800 font-medium transition-colors"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                                    Back
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={loading || selectedPages.size === 0}
                                    className={`
                                        flex items-center px-8 py-3.5 rounded-xl font-semibold text-white
                                        transition-all duration-200 transform
                                        ${loading || selectedPages.size === 0
                                            ? 'bg-gray-400 cursor-not-allowed' 
                                            : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 hover:scale-105 shadow-lg hover:shadow-xl'
                                        }
                                    `}
                                >
                                    {loading ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                            Starting...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSpider} className="mr-2" />
                                            Start Crawling {selectedPages.size} {selectedPages.size === 1 ? 'Page' : 'Pages'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default ClientForm;
